import Header from "../../common/Header";
import Footer from "../../common/Footer";
import darkLogo from "../../../images/logo-black.png";
import lightLogo from "../../../images/logo-white.png";
import { Link } from "react-router-dom";

const TermsAndConditions = ({ mode = "light" }) => {
  return (
    <div>
      <div className="relative container mx-auto max-w-screen-2xl">
        <Header mode="light" className="border-b border-gray-3" />

        <div className="flex flex-col mt-10 md:mt-[54px] mb-8 md:mb-[76px] px-6 md:px-[48px] lg:px-[128px]">
          <h1 className="font-[900] md:font-extrabold text-[32px] md:text-[50px] leading-[38px] md:leading-[52px] text-black text-center mb-6 md:mb-10">
            Terms & Conditions
          </h1>

          <div className="flex flex-col gap-6 md:gap-8 mb-6 md:mb-[97px]">
            {/* Introduction */}
            <div>
              <h2 className="font-bold text-lg md:text-xl mb-2 text-black">
                1. Introduction
              </h2>
              <p className="font-normal text-sm md:text-base leading-[18px] md:leading-[22px] text-black">
                Welcome to Unspokenn, a platform dedicated to empowering
                individuals to report incidents safely and anonymously. These
                Terms and Conditions ("Terms") govern your access to and use of
                the Unspokenn platform, website, and services (collectively, the
                "Service"). By accessing or using the Service, you agree to be
                bound by these Terms. If you disagree with any part of these
                Terms, you may not access the Service.
              </p>
            </div>

            {/* Definitions */}
            <div>
              <h2 className="font-bold text-lg md:text-xl mb-2 text-black">
                2. Definitions
              </h2>
              <ul className="list-disc pl-5 font-normal text-sm md:text-base leading-[18px] md:leading-[22px] text-black">
                <li className="mb-1">
                  "Unspokenn," "we," "us," and "our" refer to the Unspokenn
                  platform and its operators.
                </li>
                <li className="mb-1">
                  "User," "you," and "your" refer to individuals who access or
                  use the Service.
                </li>
                <li className="mb-1">
                  "Content" refers to all information, data, text, reports, and
                  other materials submitted, posted, or displayed by users on
                  the Service.
                </li>
                <li>
                  "Report" refers to any incident, observation, or experience
                  shared by a user through the Service.
                </li>
              </ul>
            </div>

            {/* User Eligibility */}
            <div>
              <h2 className="font-bold text-lg md:text-xl mb-2 text-black">
                3. User Eligibility
              </h2>
              <p className="font-normal text-sm md:text-base leading-[18px] md:leading-[22px] text-black">
                You must be at least 18 years old to use the Service. By using
                the Service, you represent and warrant that you meet this
                requirement. If you are accessing the Service on behalf of an
                organization, you represent and warrant that you have the
                authority to bind that organization to these Terms.
              </p>
            </div>

            {/* Anonymous Reporting */}
            <div>
              <h2 className="font-bold text-lg md:text-xl mb-2 text-black">
                4. Anonymous Reporting
              </h2>
              <p className="font-normal text-sm md:text-base leading-[18px] md:leading-[22px] text-black mb-2">
                <strong>4.1 Purpose:</strong> Unspokenn provides a platform for
                anonymous reporting of non-emergency incidents, helping to
                promote safety, accountability, and transparency within
                communities.
              </p>
              <p className="font-normal text-sm md:text-base leading-[18px] md:leading-[22px] text-black mb-2">
                <strong>4.2 Anonymity Protection:</strong> We implement
                technical and organizational measures designed to protect your
                anonymity when submitting reports. However, while we take
                significant steps to maintain your anonymity, we cannot
                guarantee absolute anonymity in all circumstances, particularly
                in cases involving legal obligations or sophisticated technical
                attacks.
              </p>
              <p className="font-normal text-sm md:text-base leading-[18px] md:leading-[22px] text-black">
                <strong>4.3 Report Content:</strong> You are solely responsible
                for the content of your reports. Reports should be truthful,
                accurate, and related to legitimate safety or accountability
                concerns.
              </p>
            </div>

            {/* Prohibited Activities */}
            <div>
              <h2 className="font-bold text-lg md:text-xl mb-2 text-black">
                5. Prohibited Activities
              </h2>
              <p className="font-normal text-sm md:text-base leading-[18px] md:leading-[22px] text-black mb-2">
                When using the Service, you agree not to:
              </p>
              <ul className="list-decimal pl-5 font-normal text-sm md:text-base leading-[18px] md:leading-[22px] text-black">
                <li className="mb-1">
                  Submit false, misleading, or malicious reports intended to
                  harm others.
                </li>
                <li className="mb-1">
                  Use the Service to threaten, harass, defame, or intimidate
                  others.
                </li>
                <li className="mb-1">
                  Submit reports about emergency situations that require
                  immediate response from emergency services.
                </li>
                <li className="mb-1">
                  Attempt to identify anonymous users of the platform.
                </li>
                <li className="mb-1">
                  Circumvent, disable, or otherwise interfere with
                  security-related features of the Service.
                </li>
                <li className="mb-1">
                  Use the Service for any illegal purpose or in violation of any
                  local, state, national, or international law.
                </li>
                <li className="mb-1">
                  Interfere with or disrupt the Service or servers or networks
                  connected to the Service.
                </li>
                <li>
                  Upload or transmit viruses, malware, or other types of
                  malicious software.
                </li>
              </ul>
            </div>

            {/* Data Privacy and Security */}
            <div>
              <h2 className="font-bold text-lg md:text-xl mb-2 text-black">
                6. Data Privacy and Security
              </h2>
              <p className="font-normal text-sm md:text-base leading-[18px] md:leading-[22px] text-black mb-2">
                <strong>6.1 Data Collection:</strong> We collect and process
                information as described in our Privacy Policy. By using the
                Service, you consent to such collection and processing.
              </p>
              <p className="font-normal text-sm md:text-base leading-[18px] md:leading-[22px] text-black mb-2">
                <strong>6.2 Security Measures:</strong> We implement reasonable
                security measures to protect the personal information that is
                collected and to maintain the anonymity of reports. These
                measures include encryption, secure data storage, and controlled
                access to information.
              </p>
              <p className="font-normal text-sm md:text-base leading-[18px] md:leading-[22px] text-black">
                <strong>6.3 Disclosure to Authorities:</strong> In certain
                circumstances, we may be legally required to disclose
                information to law enforcement or other authorities. We will
                only do so when required by law, court order, or similar legal
                procedure.
              </p>
            </div>

            {/* More sections can be added, but to keep the example concise, I've included the first 6 sections */}
            <div>
              <p className="font-normal text-sm md:text-base leading-[18px] md:leading-[22px] text-black italic">
                For the complete Terms and Conditions, including Content
                Moderation, Intellectual Property, Limitation of Liability,
                Disclaimer of Warranties, Indemnification, Termination, Changes
                to Terms, Governing Law, Severability, and Contact Information
                sections, please visit our full Terms and Conditions page or
                contact us at support@unspokenn.com.
              </p>
              <p className="font-normal text-sm md:text-base leading-[18px] md:leading-[22px] text-black mt-4">
                Last Updated: March 20, 2025
              </p>
            </div>
          </div>

          <div className="flex flex-col items-center max-w-[175px] self-center">
            <Link to="/" className="">
              <img
                src={mode === "dark" ? lightLogo : darkLogo}
                className="h-[17px] md:h-[25.32px]"
                alt="unspokenn"
              />
            </Link>

            <p className="font-medium md:font-normal text-[8px] xs:text-xs leading-[11.76px] xs:leading-[14.32px] mt-3 md:mt-[18px] text-center">
              Copyright © UNSPOKEN 2024
              <br /> All Right Reserved
            </p>
          </div>
        </div>
        <Footer mode="dark" />
      </div>
    </div>
  );
};

export default TermsAndConditions;
